// Generic localStorage-backed JSON store with cross-document change events.
// Used by bookmarks, history, notes, and other persisted features.

const channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("revault:data") : null;
// Node (unit tests) keeps a process alive for an open channel; browsers have no unref.
if (channel && typeof channel.unref === "function") channel.unref();
const subs = new Map(); // key -> Set<fn>

export function readJSON(key, fallback) {
	try { const v = localStorage.getItem(key); return v == null ? fallback : JSON.parse(v); }
	catch (e) { return fallback; }
}

// Returns false when the browser refused the write (usually out of space). Then nothing is
// broadcast, and "rv:save-failed" goes to this window so the app can say "Not saved".
export function writeJSON(key, val) {
	try { localStorage.setItem(key, JSON.stringify(val)); }
	catch {
		try { window.dispatchEvent(new CustomEvent("rv:save-failed", { detail: { key } })); } catch { /* no window */ }
		return false;
	}
	if (channel) channel.postMessage(key);
	notify(key);
	return true;
}

export function remove(key) {
	try { localStorage.removeItem(key); } catch (e) {}
	if (channel) channel.postMessage(key);
	notify(key);
}

// Notify subscribers + other tabs WITHOUT writing (for callers that already wrote
// the value themselves, e.g. after probing the quota explicitly).
export function broadcast(key) {
	if (channel) channel.postMessage(key);
	notify(key);
}

export function subscribe(key, fn) {
	if (!subs.has(key)) subs.set(key, new Set());
	subs.get(key).add(fn);
	return () => { const s = subs.get(key); if (s) s.delete(fn); };
}

function notify(key) {
	const s = subs.get(key);
	if (s) s.forEach((fn) => { try { fn(); } catch (e) {} });
}

// A broadcast only names a key to re-read; subscribers read the value from localStorage. Any page on
// this origin can post here, so anything but a key name (a string) is ignored.
if (channel) channel.onmessage = (e) => { if (typeof e.data === "string") notify(e.data); };
if (typeof window !== "undefined") {
	window.addEventListener("storage", (e) => { if (e.key) notify(e.key); });
}
